// Iteration 47 Phase 3 — Admin Duplicates review page.
// Non-destructive. Surfaces job + invoice duplicate clusters.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertTriangle, Link2, RefreshCw, ExternalLink, Loader2, ChevronDown, ChevronRight } from "lucide-react";

export default function AdminDuplicates() {
  const [entity, setEntity] = useState("jobs");
  const [windowDays, setWindowDays] = useState(3);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [linking, setLinking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workflow/duplicates/scan?entity=${entity}&window_days=${windowDays}`);
      setClusters(r.data.clusters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [entity, windowDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkAsRelated = async (primary, others) => {
    setLinking(primary);
    try {
      for (const other of others) {
        await api.post(`/workflow/jobs/${primary}/link-related`, {
          related_job_id: other, relation: "duplicate_review",
        });
      }
      toast.success(`Linked ${others.length} job(s) to ${primary.slice(0, 8)}…`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally { setLinking(null); }
  };

  return (
    <div className="space-y-6 pb-16">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Duplicate Review
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Non-destructive scan. Same VIN in different jobs can be legitimate — review before linking.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="border-slate-700" data-testid="rescan-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Rescan
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-slate-900/40 rounded-lg p-3 border border-slate-800">
        <div className="flex items-center gap-1">
          <label className="text-xs text-slate-400">Entity:</label>
          <select
            value={entity} onChange={(e) => setEntity(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
            data-testid="entity-select"
          >
            <option value="jobs">Jobs</option>
            <option value="invoices">Invoices</option>
          </select>
        </div>
        {entity === "jobs" && (
          <div className="flex items-center gap-1">
            <label className="text-xs text-slate-400">Window (days):</label>
            <input
              type="number" min="1" max="30" value={windowDays}
              onChange={(e) => setWindowDays(parseInt(e.target.value) || 3)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm w-16"
              data-testid="window-days"
            />
          </div>
        )}
        <div className="ml-auto text-xs text-slate-400" data-testid="cluster-count">
          <span className="text-amber-400 font-semibold">{clusters.length}</span> cluster(s)
        </div>
      </div>

      <div className="space-y-3">
        {clusters.length === 0 && !loading && (
          <div className="text-center py-12 text-slate-500 text-sm">
            No duplicates detected. Good hygiene ✓
          </div>
        )}
        {clusters.map((c, idx) => {
          const key = c.vin || c.job_id || idx;
          const isOpen = expanded[key];
          const items = c.jobs || c.invoices || [];
          const isJobs = entity === "jobs";
          return (
            <div key={key} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`cluster-${idx}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  className="flex items-center gap-2 text-left flex-1"
                  onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div>
                    <div className="font-semibold text-sm">
                      {isJobs ? `VIN ${c.vin}` : `Job ${(c.job_id || "").slice(0, 8)}… (${c.type})`}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {c.count} {isJobs ? "jobs" : "invoices"} · {c.reason || "same job + type"}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-500/20 border border-amber-500/40 text-amber-200">
                    {c.count} items
                  </Badge>
                  {isJobs && items.length >= 2 && (
                    <Button
                      size="sm" variant="outline"
                      className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                      onClick={() => linkAsRelated(items[0].id, items.slice(1).map((x) => x.id))}
                      disabled={linking === items[0].id}
                      data-testid={`link-btn-${idx}`}
                    >
                      {linking === items[0].id
                        ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        : <Link2 className="w-3 h-3 mr-1" />
                      }
                      Link as related
                    </Button>
                  )}
                </div>
              </div>
              {isOpen && (
                <div className="mt-3 space-y-1 pl-6 border-l-2 border-amber-500/20">
                  {items.map((it, i) => (
                    <div key={it.id} className="flex items-center justify-between gap-2 text-xs bg-slate-900/40 rounded px-2 py-1.5" data-testid={`cluster-${idx}-item-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-slate-300 truncate">
                          {isJobs ? (
                            <>
                              <span className="text-blue-400">{(it.id || "").slice(0, 8)}</span>
                              {" · "}
                              <span className="text-slate-400">RO {it.ro_number || "—"}</span>
                              {" · "}
                              <span className="text-slate-400">{it.service_date || it.created_at?.slice(0, 10)}</span>
                              {it.customer_name && <span className="text-slate-500"> · {it.customer_name}</span>}
                              {it.status && <Badge className="ml-2 bg-slate-800 border-slate-700 text-[10px]">{it.status}</Badge>}
                            </>
                          ) : (
                            <>
                              <span className="text-blue-400">{it.invoice_number || (it.id || "").slice(0, 8)}</span>
                              <span className="ml-2 text-slate-400">${(it.total || 0).toFixed(2)}</span>
                              {it.status && <Badge className="ml-2 bg-slate-800 border-slate-700 text-[10px]">{it.status}</Badge>}
                            </>
                          )}
                        </div>
                      </div>
                      <Link
                        to={isJobs ? `/jobs/${it.id}` : `/invoices/${it.id}`}
                        className="text-blue-400 hover:underline flex items-center gap-1 text-xs"
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
